/*----------------------------------
- DOMAINING NEWS LEAD EDITION
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import CtaPill from '@uniquedomains/components/website/WebsiteCta';

import {
    domainingNewsEditionPath,
    type PublishedNewsEdition,
} from '@uniquedomains/components/contracts/news/public';
import {
    formatDomainingNewsLongDate,
    getDomainingNewsCategoryShortLabel,
} from '@uniquedomains/components/news/presentation';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainingNewsLeadEditionProps = {
    edition: PublishedNewsEdition;
    class?: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** Outline pill naming a category. Same geometry as the chips on the article header. */
const CATEGORY_CHIP_CLASS =
    'rounded-full border border-[rgba(27,29,30,0.12)] px-3 py-[7px] text-[11.5px] font-semibold text-[#66696b] lg:px-3.5 lg:text-[12.5px]';

/*----------------------------------
- COMPONENT
----------------------------------*/

/**
 * The newest edition, opening the archive. The takeaways read as a stacked list on a
 * phone and as the mockup's three tinted tiles from `lg` up, from the same markup.
 */
export default function DomainingNewsLeadEdition({
    edition,
    class: className = '',
}: DomainingNewsLeadEditionProps) {
    const editionPath = domainingNewsEditionPath(edition.dateKey);

    return (
        <article
            class={[
                'overflow-hidden rounded-[24px] border border-[rgba(27,29,30,0.07)] bg-white',
                'p-5 pb-[22px] shadow-[0_18px_50px_rgba(27,29,30,0.06)] lg:rounded-[28px] lg:p-10 lg:px-11',
                className,
            ].join(' ')}
            data-testid="domaining-news-lead-edition"
        >
            <div class="flex flex-wrap items-center gap-x-2.5 gap-y-2 lg:gap-x-3">
                <span class="inline-flex rounded-full bg-[#1b1d1e] px-2.5 py-[5px] text-[10px] font-extrabold uppercase tracking-[0.09em] text-white lg:px-3 lg:text-[10.5px]">
                    Latest
                </span>
                <time
                    dateTime={edition.dateKey}
                    class="text-[13px] font-semibold tabular-nums text-[#66696b] lg:text-sm"
                >
                    {formatDomainingNewsLongDate(edition.dateKey)}
                </time>
                <span class="text-[13px] text-[#66696b] lg:text-sm">
                    {`· ${edition.sourceCount} ${edition.sourceCount === 1 ? 'source' : 'sources'} · ${edition.readingMinutes} min read`}
                </span>
            </div>

            <h2 class="mt-4 max-w-[960px] text-pretty text-[27px] font-bold leading-[1.08] tracking-[-0.04em] text-[#1b1d1e] lg:mt-5 lg:text-[46px] lg:leading-[1.05] lg:tracking-[-0.045em]">
                <a href={editionPath} class="transition hover:opacity-80">
                    {edition.headline}
                </a>
            </h2>
            <p class="mt-3.5 max-w-[760px] text-[15px] leading-[1.6] text-[#66696b] lg:mt-[18px] lg:text-[17px]">
                {edition.summary}
            </p>

            {edition.takeaways.length ? (
                <ol class="mt-5 flex flex-col gap-2.5 lg:mt-7 lg:grid lg:grid-cols-3">
                    {edition.takeaways.slice(0, 3).map((takeaway, index) => (
                        <li
                            key={`${index}-${takeaway.text.slice(0, 32)}`}
                            class="flex items-start gap-2.5 lg:flex-col lg:rounded-[18px] lg:bg-[#f8f6f1] lg:px-[18px] lg:py-4"
                        >
                            <span
                                aria-hidden="true"
                                class="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f2e97e] text-[10.5px] font-extrabold tabular-nums text-[#1b1d1e] lg:h-6 lg:w-6 lg:text-[11px]"
                            >
                                {index + 1}
                            </span>
                            <span class="text-sm leading-[1.55] text-[#4a4d4f] lg:text-[14.5px]">
                                {takeaway.text}
                            </span>
                        </li>
                    ))}
                </ol>
            ) : null}

            <div class="mt-5 flex flex-wrap items-center gap-2.5 lg:mt-[26px] lg:gap-3">
                <CtaPill href={editionPath} variant="accent" size="md">
                    Read the full brief
                </CtaPill>
                {edition.categoryCodes.slice(0, 2).map((code) => (
                    <span key={code} class={CATEGORY_CHIP_CLASS}>
                        {getDomainingNewsCategoryShortLabel(code)}
                    </span>
                ))}
            </div>
        </article>
    );
}
